import { GroupedObjectInfo } from '@fullstory/renderer-shared';
import React, { memo, useMemo } from 'react';
import css from './ObjectHighlight.module.scss';

export type GroupedObjectInfoWithLabel = GroupedObjectInfo & {
  label?: string;
};

export type ObjectCollectionWithLabels = { [path: string]: GroupedObjectInfoWithLabel };

export type HighlightType =
  | 'inspected'
  | 'inspected-context'
  | 'hovered'
  | 'existing-context'
  | 'suggestion-context';

const overlayRectStyles: React.CSSProperties = {
  position: 'absolute',
  boxSizing: 'border-box',
  pointerEvents: 'none',
  borderRadius: '2px',
  top: `calc(var(--top) - 1px)`,
  left: `calc(var(--left) - 1px)`,
  width: `calc(var(--width) + 2px)`,
  height: `calc(var(--height) + 2px)`,
};

export type RectHighlightProps = {
  /** The rect to highlight. */
  rect: DOMRect;
  /** If provided, a standard highlight style to apply. */
  type?: HighlightType;
  /** If provided, custom styles to apply to each highlight. */
  style?: React.CSSProperties;
  /** If provided, callbacks to call when the rect is interacted with. */
  pointerEvents?: {
    onClick: () => void;
    onMouseEnter: () => void;
    onMouseMove: (event: React.MouseEvent) => void;
    onMouseLeave: () => void;
  };
};

export const RectHighlight: React.FC<RectHighlightProps> = memo(function RectHighlight({
  rect,
  style,
  type,
  pointerEvents,
}) {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      style={
        {
          ...overlayRectStyles,
          '--top': `${rect.top}px`,
          '--left': `${rect.left}px`,
          '--width': `${rect.width}px`,
          '--height': `${rect.height}px`,
          ...(pointerEvents ? { pointerEvents: 'auto', cursor: 'pointer' } : {}),
          ...style,
        } as React.CSSProperties
      }
      className={type ? css[type] : undefined}
      onClick={pointerEvents?.onClick}
      onMouseEnter={pointerEvents?.onMouseEnter}
      onMouseMove={pointerEvents?.onMouseMove}
      onMouseLeave={pointerEvents?.onMouseLeave}
    />
  );
});

type ObjectPointerEvents = {
  onClick: (object: GroupedObjectInfoWithLabel) => void;
  onMouseEnter: (object: GroupedObjectInfoWithLabel, type: string | undefined) => void;
  onMouseMove: (event: React.MouseEvent) => void;
  onMouseLeave: (object: GroupedObjectInfoWithLabel, type: string | undefined) => void;
};

export type ObjectHighlightProps = {
  /** The object to highlight. */
  object: GroupedObjectInfoWithLabel;
  /** If provided, a standard highlight style to apply. */
  type?: HighlightType;
  /** If provided, custom styles to apply to each highlight. */
  style?: React.CSSProperties;
  /** If provided, callbacks to call when the rect is interacted with. */
  pointerEvents?: ObjectPointerEvents;
};

export const ObjectHighlight: React.FC<ObjectHighlightProps> = memo(function ObjectHighlight({
  object,
  style,
  type,
  pointerEvents,
}) {
  const pointerEventsRect = useMemo(() => {
    if (pointerEvents) {
      return {
        onClick: () => pointerEvents.onClick(object),
        onMouseEnter: () => pointerEvents.onMouseEnter(object, type),
        onMouseMove: pointerEvents.onMouseMove,
        onMouseLeave: () => pointerEvents.onMouseLeave(object, type),
      };
    }
    return undefined;
  }, [object, type, pointerEvents]);

  return (
    <RectHighlight rect={object.rect} style={style} type={type} pointerEvents={pointerEventsRect} />
  );
});

export type ObjectCollectionHighlightProps = {
  /** The collection of objects to highlight. */
  objects: ObjectCollectionWithLabels;
  /** If provided, only highlight objects tagged with this group. */
  group?: string;
  /** If provided, a standard highlight style to apply. */
  type?: HighlightType;
  /** If provided, custom styles to apply to each highlight. */
  style?: React.CSSProperties;
  /** If provided, callbacks to call when the rect is interacted with. */
  pointerEvents?: ObjectPointerEvents;
};

export const ObjectCollectionHighlight: React.FC<ObjectCollectionHighlightProps> = memo(
  function ObjectCollectionHighlight({ group, objects, style, type, pointerEvents }) {
    return (
      <>
        {[...Object.values(objects)]
          .filter(({ groups }) => typeof group === 'undefined' || groups.includes(group))
          .map(object => (
            <ObjectHighlight
              key={object.path}
              object={object}
              style={style}
              type={type}
              pointerEvents={pointerEvents}
            />
          ))}
      </>
    );
  },
);
